import React, { useState } from 'react';

/** 아이디 규칙 — 서버(routes/auth.ts checkUserId)와 같다. 화면에서 먼저 알려 준다 */
export function userIdError(v: string): string | null {
  if (!v) return '새 아이디를 입력하세요.';
  if (v.length < 2 || v.length > 20) return '아이디는 2~20자여야 합니다.';
  if (/\s/.test(v)) return '아이디에 공백은 넣을 수 없습니다.';
  if (v.includes('#')) return "'#' 는 쓸 수 없습니다.";
  return null;
}

/**
 * **새 아이디 칸 + [변경]** — 내 아이디 바꾸기(PATCH /api/auth/my-user-id).
 * 프로필 창 [아이디 변경] 과, 운영자가 아이디를 초기화한 뒤 들어오면 뜨는 아이디 변경 창(IdResetModal)이 같이 쓴다.
 * 안내 문구는 두지 않는다(사용자 2026-10-27 "이거 없애") — 규칙에 어긋나거나 바꾸면 그때 알려 준다.
 */
export default function UserIdForm({ current, onChanged, doneText }: {
  /** 지금 아이디 — 같은 걸 넣으면 서버에 보내지 않는다 */
  current?: string;
  onChanged: (id: string) => void;
  /** 바꾼 뒤 문구 (없으면 '다음 로그인부터 새 아이디로') */
  doneText?: (id: string) => string;
}) {
  const [newId, setNewId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async () => {
    const next = newId.trim();
    const bad = userIdError(next);
    if (bad) { setMsg({ ok: false, text: bad }); return; }
    if (next === current) { setMsg({ ok: false, text: '지금 아이디와 같습니다.' }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const r = await fetch('/api/auth/my-user-id', {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: next })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) { setMsg({ ok: false, text: d.error || '아이디를 바꾸지 못했습니다.' }); return; }
      setNewId('');
      setMsg({ ok: true, text: doneText ? doneText(next) : `아이디를 '${next}' 로 바꿨습니다. 다음 로그인부터 새 아이디로 들어오세요.` });
      onChanged(next);
    } catch {
      setMsg({ ok: false, text: '서버에 닿지 못했습니다.' });
    } finally {
      setBusy(false);
    }
  };

  const off = busy || !newId.trim();
  return (
    <>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={newId}
          onChange={(e) => { setNewId(e.target.value); setMsg(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !busy) submit(); }}
          placeholder="새 아이디"
          maxLength={20}
          autoFocus
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          style={{
            flex: 1, minWidth: 0, padding: '9px 11px', borderRadius: 10, border: '1px solid #d5dae1',
            fontSize: 16, color: '#222', background: '#fff', outline: 'none', boxSizing: 'border-box'
          }}
        />
        <button
          type="button"
          onClick={submit}
          disabled={off}
          style={{
            padding: '0 14px', borderRadius: 10, border: 'none', fontWeight: 800, fontSize: 14, flexShrink: 0,
            background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff',
            cursor: off ? 'default' : 'pointer', opacity: off ? 0.5 : 1
          }}
        >{busy ? '바꾸는 중' : '변경'}</button>
      </div>
      {msg && (
        <div style={{ fontSize: 12.5, fontWeight: 700, color: msg.ok ? '#16a34a' : '#e53935', lineHeight: 1.45 }}>{msg.text}</div>
      )}
    </>
  );
}
